/*
 * ParcelOverview.tsx
 * Author: fwunderlich
 * Date: 01.12.2023
 *
 * Copyright: DMT GmbH & Co. KG
 */

import React, {useEffect, useRef, useState} from 'react';
import CustomDataTable, {ColumnObject} from '../../global/CustomDataTable';
import {PersonOverview} from '../person/PersonOverview';
import {ParcelDialogEditForm} from './ParcelDialogEditForm';
import {checkPermission} from '../../../functions/functionLibrary';
import {permissions} from '../../../config/permissions';
import {useDeleteParcelMutation, useGetParcelQuery} from '../../../redux/rtk/injectedParcelApi';
import {Parcel} from '../../functions/GlobalData';
import {useMapper} from '../../hooks/useMapper';
import {useTranslation} from 'react-i18next';
import {LandRegisterOverview} from '../landRegister/LandRegisterOverview';
import {useGetPersonenTypesQuery, useSetStatusMutation} from '../../../redux/rtk/injectedMappingApi';
import {getColumnsParcel, getFiltersParcel, onClickSetSelectionStatus} from '../../functions/GlobalData';
import {classNames} from 'primereact/utils';
import {ProjectList} from '../ProjectList';
import CustomDialog from '../../global/CustomDialog';
import {ParcelEditSelectionForm} from './ParcelEditSelectionForm';
import {Button} from 'primereact/button';
import {Menu} from 'primereact/menu';
import {CustomConfirmDialog} from '../../global/CustomConfirmDialog';
import {DataToProject} from '../DataToProject';
import {useAppDispatch, useAppSelector} from '../../../redux/hooks';
import {exportPdf, setSelectedFeatures} from '../../../redux/slices/GeomonitoringSlice';
import {UtilisationOverview} from '../../projectMasterData/utilisation/UtilisationOverview';
import {ProcessOverview} from '../process/ProcessOverview';
import {MeasureOverview} from '../../projectMasterData/measure/MeasureOverview';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faMap} from '@fortawesome/free-solid-svg-icons';
import {useNavigate} from 'react-router-dom';
import {exportCSV} from '../../functions/Global';

type Props = {
    projectId: any,
    editable: boolean,
    expandable: boolean,
    hideGlobalFilter: boolean,
    onFinished?: (data: Parcel) => void,
    commonData: boolean,
    showColumnFilters: boolean,
    showPersons: boolean,
    showLandRegisters: boolean,
    showShareInfos: boolean,
    showUtilisationInfo: boolean,
    hideButtons?: boolean,
    processId?: string,
    mapping?: any,
    selectable?: boolean,
    multiSelection?: boolean,
    onSelection?: (e: Parcel[]) => void,
    className?: string,
    runRefetch?: boolean,
    scrollHeight?: string,
    skeletonCount?: number,
    geoData: any,
    isDirty?: boolean,
    ableToUpdate?: boolean,
}

const DATATABLE_ID = 'parcelDataTable';

export const ParcelOverview = (props: Props) => {

    const navigate = useNavigate();
    const {t} = useTranslation(['common', 'globalData']);
    const status = useAppSelector((state: any) => state.importData);

    const menu = useRef<any>(null);
    const dispatch = useAppDispatch();
    const [values, setValues] = useState<any>([]);
    const [selectedValue, setSelectedValue] = useState<Parcel | undefined>(undefined);
    const [showValue, setShowValue] = useState<boolean>(false);
    const [showCreateParcel, setShowCreateParcel] = useState<boolean>(false);
    const [showEditParcelSelection, setShowEditParcelSelection] = useState<boolean>(false);
    const [editValue, setEditValue] = useState<boolean>(false);
    const [showAddParcelToProject, setShowAddParcelToProject] = useState<boolean>(false);

    const [dataLoaded, setDataLoaded] = useState(false);
    const [selection, setSelection] = useState<any>([]);
    const [expandedRows, setExpandedRows] = useState<any>(null);
    const [columns, setColumns] = useState<ColumnObject[]>([]);
    const [returnedData, setReturnedData] = useState<Parcel>();

    const [personTypes, setPersonTypes] = useState<any>([]);

    const personTypesQuery = useGetPersonenTypesQuery({});
    const [deleteParcel, resultDeleteParcel] = useDeleteParcelMutation();
    const [setStatus, resultSetStatus] = useSetStatusMutation();
    const printOrder = useAppSelector((state: any) => state.configLayer.layerData.exportPdf);

    const [isDirty, setIsDirty] = useState<any>(props.isDirty);

    const mappedArray = useMapper(returnedData, values, 'flurstueck_id');

    const {
        data: parcel = undefined, isLoading,
        isSuccess,
        isFetching,
        isError,
        error,
        refetch,
    } = useGetParcelQuery({
        projectId: props.projectId,
        commonData: props.commonData,
        // flurstueckId: props.dataIds ? props.dataIds.length > 0 ? `["${props.dataIds.join('","')}"]` : [] : undefined
        mapping: props.mapping
    });

    if (isError) {
        console.log({error});
    }

    useEffect(() => {
        if (window.location.hash.indexOf('_') > -1 && parcel) {
            const hashElements = window.location.hash.split('_');
            if (hashElements.length > 2) {
                const id = hashElements[2];
                const sParcel = parcel.find((item: Parcel) => {
                    return item.flurstueck_id === id;
                });
                if (sParcel) {
                    setSelectedValue(sParcel);
                    setEditValue(true);
                    setShowValue(true);
                }
            }
        }
    }, [parcel]);

    useEffect(() => {
        setIsDirty(props.isDirty);
    }, [props.isDirty]);

    useEffect(() => {
        if (personTypesQuery.data) {
            setPersonTypes(personTypesQuery.data.map((x: any) => {
                return {label: x.name, value: x.id};
            }));
        }
    }, [personTypesQuery]);

    useEffect(() => {
        setColumns(getColumnsParcel(parcel, t, personTypes.data, props.showPersons, props.showLandRegisters, props.mapping, props.showUtilisationInfo));
        if (parcel) {
            const expandedRowsTemp: any[] = [];
            const newArray = JSON.parse(JSON.stringify(parcel));
            newArray.map((r: Parcel) => {
                    //  console.log('person3');
                    if (r.vorgang || r.inanspruchnahme || r.massnahme || r.person || r.grundbuch || r.inanspruchnahme)
                        r.expandableElement = (
                            <>
                                {r.vorgang && r.vorgang[0] !== null && <ProcessOverview
                                    showParcels={true}
                                    showPersons={false}
                                    showLandRegisters={false}
                                    mapping={{flurstueck_id: r.flurstueck_id}}
                                    projectId={props.projectId}
                                    editable={false}
                                    expandable={false}
                                    hideGlobalFilter={false}
                                    showColumnFilters={false}
                                    skeletonCount={1}
                                />}
                                {r.person && r.person[0] !== null && <PersonOverview
                                    showParcels={true}
                                    showPersons={false}
                                    showLandRegisters={false}
                                    showShareInfos={true}
                                    showSuggestions={false}
                                    hideButtons={true}
                                    mapping={{flurstueck_id: r.flurstueck_id}}
                                    projectId={props.projectId}
                                    editable={false}
                                    expandable={false}
                                    hideGlobalFilter={false}
                                    commonData={props.commonData}
                                    showColumnFilters={false}
                                    skeletonCount={1}
                                    geoData={props.geoData}
                                />}
                                {r.grundbuch && r.grundbuch[0] !== null && <LandRegisterOverview
                                    showPersons={false}
                                    showParcels={false}
                                    showShareInfos={false}
                                    hideButtons={true}
                                    projectId={props.projectId}
                                    mapping={{flurstueck_id: r.flurstueck_id}}
                                    editable={false}
                                    expandable={false}
                                    hideGlobalFilter={false}
                                    commonData={true}
                                    showColumnFilters={false}
                                    skeletonCount={1}
                                    geoData={props.geoData}
                                />}

                                {r.inanspruchnahme && r.inanspruchnahme[0] !== null && <UtilisationOverview
                                    projectId={props.projectId}
                                    editable={false}
                                    expandable={true}
                                    hideButtons={true}
                                    mapping={{flurstueck_id: r.flurstueck_id}}
                                    hideGlobalFilter={false}
                                    showColumnFilters={false}
                                    skeletonCount={1}/>}
                                {r.massnahme && r.massnahme[0] !== null && <MeasureOverview
                                    projectId={props.projectId}
                                    editable={false}
                                    expandable={true}
                                    hideButtons={true}
                                    mapping={{flurstueck_id: r.flurstueck_id}}
                                    hideGlobalFilter={false}
                                    showColumnFilters={false}
                                    skeletonCount={1}/>}
                                {props.projectId === '' &&
                                <ProjectList projectId={props.projectId} mapping={{flurstueck_id: r.flurstueck_id}}
                                             hideGlobalFilter={false} showColumnFilters={true}/>}
                            </>);

                    setExpandedRows(expandedRowsTemp);
                }
            );

            setValues(newArray);
            setDataLoaded(true);
            // setSkip(true);
        }
    }, [parcel]);

    useEffect(() => {
        if (typeof returnedData !== 'undefined') {
            setValues(mappedArray);
        }
    }, [returnedData]);

    useEffect(() => {
        if (props.onSelection) {
            props.onSelection(selection);
        }
        const kennzArray: any = [];
        selection.forEach((sel: any) => {
            // console.log(sel)
            kennzArray.push(sel.flstkennz);
        });
        dispatch(setSelectedFeatures(kennzArray));
    }, [selection]);

    const hideDialog = () => {
        setShowEditParcelSelection(false);
        setShowAddParcelToProject(false);
    };


    const getButtons = () => {
        const result: any[] = [
            {
                label: t('customDataTable:tooltips.exportAsCSV'),
                icon: 'pi pi-file-export',
                disabled: false,
                command: () => {
                    exportCSV(t, values, DATATABLE_ID, columns);
                }
            },
            {
                label: t('globalData:buttons.editSelection'),
                icon: 'pi pi-pencil',
                disabled: selection.length <= 0,
                command: onClickEditSelection
            },
            {
                label: t('globalData:buttons.setSelectionActive'),
                icon: 'pi pi-pencil',
                disabled: selection.length <= 0,
                command: onClickSelectionActivatePersons
            },
            {
                label: t('globalData:buttons.setSelectionInactive'),
                icon: 'pi pi-pencil',
                disabled: selection.length <= 0,
                command: onClickSelectionDeactivatePersons
            },
            {
                label: props.projectId ? t('globalData:buttons.removeFromProject') : t('globalData:buttons.deleteSelection'),
                icon: 'pi pi-trash',
                disabled: selection.length <= 0,
                command: onClickDeleteSelectionParcels
            }, {
                label: t('globalData:buttons.printPdf'),
                icon: 'pi pi-file-pdf',
                disabled: selection.length <= 0,
                command: onClickExportMap
            },
        ];

        if (!props.projectId) {
            result.push({
                label: t('globalData:buttons.addSelectionToProject'),
                icon: 'pi pi-pencil',
                disabled: selection.length <= 0,
                command: onClickAddParcelToProject
            },);
        }

        return result;
    };

    const onClickCreateParcel = () => {
        setShowCreateParcel(true);
    };

    const onClickEditSelection = () => {
        setShowEditParcelSelection(true);
    };

    const onClickSelectionActivatePersons = () => {
        onClickSetSelectionStatus(props.projectId, 0, 'flurstueck', selection, t, setStatus);
    };

    const onClickSelectionDeactivatePersons = () => {
        onClickSetSelectionStatus(props.projectId, 1, 'flurstueck', selection, t, setStatus);
    };

    const onClickDeleteSelectionParcels = (e: any) => {
        CustomConfirmDialog({
            message: t('globalData:dialogs.deleteParcelDialog.' + (selection.length > 1 ? 'plural' : 'singular')),
            header: t('confirmDialog:deleteDialogHeader'),
            translation: t,
            onConfirm: () => {
                for (let i = 0; i < selection.length; i++) {
                    const item = selection[i];
                    deleteParcel({
                        project_id: props.projectId ? props.projectId : '',
                        flurstueck_id: item.flurstueck_id,
                        common_data: true
                    });
                }
            }
        });
    };
    const onClickExportMap = (e: any) => {
        dispatch(exportPdf(!printOrder));
    };

    const onClickAddParcelToProject = (e: any) => {
        setShowAddParcelToProject(true);
    };

    return (
        <div className={classNames('h-full', props.className)}><CustomDataTable
            id={DATATABLE_ID}
            header={
                <div className={'grid'}>
                    <div className={'col'}>
                        <h4 className={'m-0'}>{t('globalData:global.flurstuecke')}</h4>
                    </div>
                    {props.hideButtons !== true && <div className={'col flex justify-content-end flex-wrap mb-auto'}>
                        <Button type={'button'}
                                className={'p-button-success p-button-icon-only p-button-outlined'}
                                icon={'pi pi-plus'} onClick={onClickCreateParcel}/>
                        <div className={'inline mx-2'}>
                            <Menu model={getButtons()} popup ref={menu} id="popup_menu"/>
                            <Button className={'p-button-icon-only p-button-outlined'} icon="pi pi-angle-down"
                                    onClick={(event) => {
                                        if (menu.current) {
                                            menu.current.toggle(event);
                                        }
                                    }} aria-controls="popup_menu" aria-haspopup/>
                        </div>
                    </div>}
                </div>
            }
            className={props.className}
            columns={columns}
            customButtons={[{
                className: 'p-button-danger',
                icons: [{key: null, value: null, icon: <i className={'pi pi-trash'}/>}],
                tooltip: t('delete'),
                tooltipOptions: {position: 'top'},
                onClick: (data: any) => {
                    CustomConfirmDialog({
                        message: t('globalData:dialogs.deleteParcelDialog.singular'),
                        header: t('confirmDialog:deleteDialogHeader'),
                        translation: t,
                        onConfirm: () => {
                            deleteParcel({
                                project_id: props.projectId ? props.projectId : '',
                                flurstueck_id: data.flurstueck_id,
                                common_data: true
                            });
                        }
                    });
                },
                permitted: props.processId != null ? false :props.projectId ? checkPermission(permissions.editParcels) : checkPermission(permissions.editMasterData) && !props.processId,
            },
                {
                    icons: [{key: null, value: null, icon: <FontAwesomeIcon icon={faMap}/>}],
                    tooltip: t('edit'),
                    tooltipOptions: {position: 'top'},
                    onClick: (data: any) => {
                        setIsDirty((prevState: boolean) => {
                            const url = `/project/${props.projectId}/#globalData_2_${data['flurstueck_id']}`;
                            if (prevState) {
                                CustomConfirmDialog({
                                    message: t('dialogs.saveChangesMessage'),
                                    header: t('dialogs.saveChangesHeader'),
                                    translation: t,
                                    onConfirm: () => {
                                        navigate(url);
                                    },
                                    rejectClassName: 'float-right p-button-secondary'
                                });
                            } else {
                                navigate(url);
                            }
                            return prevState;
                        });
                    },
                    permitted: props.processId != null,
                }]}
            scrollHeight={props.scrollHeight}
            sortable={true}
            value={values}
            filters={getFiltersParcel()}
            expandable={props.expandable}
            expandedRows={expandedRows}
            paginator={true}
            paginatorHideAll={true}
            displayColumnFilter={props.showColumnFilters}
            selectionMode={props.selectable ? 'checkbox' : undefined}
            selection={props.selectable ? selection : undefined}
            onSelectionChange={props.selectable ? (e) => {
                if (props.multiSelection) {
                    setSelection(e.value);
                } else {
                    setSelection([e.value[e.value.length - 1]]);
                }
            } : undefined}
            editable={props.projectId ? checkPermission(permissions.editParcels) : checkPermission(permissions.editMasterData) && props.editable}
            onEditClick={(data: any) => {
                setSelectedValue(data);
                setEditValue(true);
                setShowValue(true);
            }}
            ableToUpdate={props.ableToUpdate != null ? props.ableToUpdate :props.projectId ? checkPermission(permissions.editParcels) : checkPermission(permissions.editMasterData) && !props.processId}
            onRemoveClick={() => {
            }}
            dataloaded={dataLoaded}
            hideGlobalFilter={props.hideGlobalFilter}
            skeletonCount={props.skeletonCount}
        />
            {showValue && <ParcelDialogEditForm
                selectedParcel={selectedValue}
                setSelectedParcel={setSelectedValue}
                show={showValue}
                setShow={setShowValue}
                projectId={props.projectId}
                commonData={props.commonData}
                geoData={props.geoData}
                onFinished={(data: Parcel) => {
                    setReturnedData(data);
                }}
            />}
            {showCreateParcel && <ParcelDialogEditForm
                projectId={props.projectId}
                show={showCreateParcel}
                setShow={setShowCreateParcel}
                geoData={props.geoData}
                commonData={true}
            />}
            {showEditParcelSelection &&
            <CustomDialog onCancel={hideDialog} visible={showEditParcelSelection} showFooter={true}
                          formName={'edit_selection'}
                          header={t('globalData:parcels.' + (selection.length > 1 ? 'edits' : 'edit'))}>
                <ParcelEditSelectionForm projectId={props.projectId} formId={'edit_selection'}
                                         selection={selection}
                                         onFinished={hideDialog}/>
            </CustomDialog>}
            {showAddParcelToProject &&
            <CustomDialog onCancel={hideDialog} visible={showAddParcelToProject} showFooter={true}
                          formName={'parcel_to_project'}
                          header={t('globalData:global.addToProject')}>
                <DataToProject projectId={props.projectId} formId={'parcel_to_project'}
                               mappingIdName={'flurstueck_id'}
                               selection={selection}
                               onFinished={hideDialog}/>
            </CustomDialog>}
        </div>
    );
};
